import { apiClient } from '@/shared/api/http-client';

import type {
    GenerateSessionInput,
    GenerateSessionOutput,
    GetClientActivityOutput,
    GetClientServiceCodesOutput,
    GetSessionListOutput,
    ResolveActionInput,
    ScheduleSessionInput,
    ScheduleSessionOutput,
    Session,
    SessionListFilters,
    UpdateSessionInput,
} from '../model/session.types';

export const sessionsApi = {
    generate: async (
        data: GenerateSessionInput,
        signal?: AbortSignal,
    ): Promise<GenerateSessionOutput> => {
        return apiClient.post<GenerateSessionOutput>('/sessions/generate', data, signal);
    },

    list: async (
        filters: SessionListFilters = {},
        signal?: AbortSignal,
    ): Promise<GetSessionListOutput> => {
        const params = new URLSearchParams();
        if (filters.start_date) params.set('start_date', filters.start_date);
        if (filters.end_date) params.set('end_date', filters.end_date);
        if (filters.status) params.set('status', filters.status);
        if (filters.client_id) params.set('client_id', filters.client_id);
        if (filters.order) params.set('order', filters.order);
        if (filters.page_size) params.set('page_size', filters.page_size.toString());
        if (filters.cursor) params.set('cursor', filters.cursor);
        if (filters.search) params.set('search', filters.search);
        if (filters.exclude_first_generation) params.set('exclude_first_generation', 'true');
        const queryString = params.toString();
        const endpoint = `/sessions${queryString ? `?${queryString}` : ''}`;
        return apiClient.get<GetSessionListOutput>(endpoint, signal);
    },

    getById: async (sessionId: string, signal?: AbortSignal): Promise<Session> => {
        return apiClient.get<Session>(`/sessions/${sessionId}`, signal);
    },

    getClientActivity: async (
        clientId: string,
        signal?: AbortSignal,
    ): Promise<GetClientActivityOutput> => {
        const params = new URLSearchParams({ client_id: clientId });
        return apiClient.get<GetClientActivityOutput>(`/sessions/activity?${params}`, signal);
    },

    schedule: async (
        data: ScheduleSessionInput,
        signal?: AbortSignal,
    ): Promise<ScheduleSessionOutput> => {
        return apiClient.post<ScheduleSessionOutput>('/sessions', data, signal);
    },

    getClientServiceCodes: async (
        clientId: string,
        signal?: AbortSignal,
    ): Promise<GetClientServiceCodesOutput> => {
        const params = new URLSearchParams({ client_id: clientId });
        return apiClient.get<GetClientServiceCodesOutput>(
            `/sessions/service-codes?${params}`,
            signal,
        );
    },

    delete: async (sessionId: string, signal?: AbortSignal): Promise<void> => {
        return apiClient.delete<void>(`/sessions/${sessionId}`, signal);
    },

    update: (data: UpdateSessionInput, signal?: AbortSignal): Promise<void> => {
        const { id, ...body } = data;
        return apiClient.patch<void>(`/sessions/${id}`, body, signal);
    },

    resolveAction: (data: ResolveActionInput, signal?: AbortSignal) => {
        const { id, ...body } = data;
        return apiClient.post<void>(`/sessions/${id}/resolve-action`, body, signal);
    },
};
